import { CheckCircle2, AlertTriangle, ShieldAlert, XCircle, FileWarning, CircleSlash, Clock, Loader2 } from "lucide-react";

export const SAFETY = {
  NO_CHANGE: { label: "No Verified Change Detected", cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: CircleSlash },
  BLOCKED_CONFLICT: { label: "Blocked — Source Conflict", cls: "bg-rose-50 text-rose-800 border-rose-300", Icon: ShieldAlert },
  MANUAL_VERIFICATION_REQUIRED: { label: "Manual Verification Required", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: AlertTriangle },
  MANUAL_EDIT_REQUIRED: { label: "Manual Edit Required", cls: "bg-orange-50 text-orange-800 border-orange-300", Icon: FileWarning },
  PUBLISH_VERIFICATION_FAILED: { label: "Publish Verification Failed", cls: "bg-red-50 text-red-800 border-red-300", Icon: XCircle },
  PUBLISHED_VERIFIED: { label: "Published + Verified", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  READY: { label: "Ready for Review", cls: "bg-blue-50 text-blue-800 border-blue-300", Icon: Clock },
  APPROVED: { label: "Approved — Awaiting Publish", cls: "bg-indigo-50 text-indigo-800 border-indigo-300", Icon: CheckCircle2 },
  SIMULATED_DRY_RUN: { label: "Simulated (Dry Run)", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: CheckCircle2 },
  CONTENT_CHANGED: { label: "Content Changed — Re-Analyze", cls: "bg-red-50 text-red-800 border-red-300", Icon: AlertTriangle },
  REJECTED: { label: "Rejected", cls: "bg-slate-100 text-slate-500 border-slate-300", Icon: XCircle },
};

export function SafetyBadge({ status }) {
  const s = SAFETY[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: Clock };
  const { Icon } = s;
  return (
    <span data-testid="safety-state-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
}

export function ReliabilityBadge({ level }) {
  const map = {
    High: "bg-emerald-50 text-emerald-700 border-emerald-300",
    Medium: "bg-amber-50 text-amber-700 border-amber-300",
    Low: "bg-rose-50 text-rose-700 border-rose-300",
  };
  return <span className={`rounded border px-2 py-0.5 text-[11px] font-mono font-semibold ${map[level] || map.Medium}`}>{level || "—"}</span>;
}

export function ConfidenceBar({ value = 0 }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
      </div>
      <span className="diff-mono text-xs text-slate-600">{v}%</span>
    </div>
  );
}

export function ModeBadge({ mode }) {
  if (mode === "LIVE")
    return (
      <span data-testid="mode-badge" className="inline-flex items-center gap-2 rounded-full border border-amber-400 bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
        <span className="h-2 w-2 rounded-full bg-amber-600 animate-pulse-dot" /> Live Mode · Real WP Edits
      </span>
    );
  if (mode === "DRY_RUN")
    return (
      <span data-testid="mode-badge" className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">
        <span className="h-2 w-2 rounded-full bg-blue-500" /> Dry Run · Simulated Publish
      </span>
    );
  // Unknown / still checking — never imply a safe DRY RUN before the server confirms.
  return (
    <span data-testid="mode-badge" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-500">
      <span className="h-2 w-2 rounded-full bg-slate-400 animate-pulse" /> {mode === "UNKNOWN" ? "Mode Unavailable" : "Checking Mode…"}
    </span>
  );
}

export function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <div data-testid="side-by-side-diff-container" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 overflow-hidden">
        <div className="border-b border-rose-200 bg-rose-100/60 px-3 py-1.5 text-xs font-mono font-semibold uppercase tracking-wide text-rose-800">− Existing</div>
        <pre className="diff-mono whitespace-pre-wrap break-words p-3 text-xs leading-relaxed text-slate-700">
          {diff.old_segments.map((s, i) => (
            <span key={`${i}-${s.text?.slice(0, 20)}`} className={s.changed ? "bg-rose-200/70 text-rose-900 rounded px-0.5" : ""}>{s.text}</span>
          ))}
        </pre>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 overflow-hidden">
        <div className="border-b border-emerald-200 bg-emerald-100/60 px-3 py-1.5 text-xs font-mono font-semibold uppercase tracking-wide text-emerald-800">+ Proposed Patch</div>
        <pre className="diff-mono whitespace-pre-wrap break-words p-3 text-xs leading-relaxed text-slate-700">
          {diff.new_segments.map((s, i) => (
            <span key={`${i}-${s.text?.slice(0, 20)}`} className={s.changed ? "bg-emerald-200/70 text-emerald-900 rounded px-0.5" : ""}>{s.text}</span>
          ))}
        </pre>
      </div>
    </div>
  );
}

export const VERIFIED_STATUS = {
  VERIFIED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  OUTDATED: "bg-red-50 text-red-800 border-red-300",
  INCORRECT: "bg-red-50 text-red-800 border-red-300",
  PARTIALLY_CORRECT: "bg-amber-50 text-amber-800 border-amber-300",
  CONFLICTING: "bg-rose-50 text-rose-800 border-rose-300",
  UNVERIFIED: "bg-yellow-50 text-yellow-800 border-yellow-300",
};

export function VerifiedStatusBadge({ status }) {
  const cls = VERIFIED_STATUS[status] || "bg-slate-100 text-slate-600 border-slate-300";
  const dot = status === "VERIFIED" ? "🟢" : status === "UNVERIFIED" ? "🟡" : status === "PARTIALLY_CORRECT" ? "🟠" : "🔴";
  return (
    <span data-testid="verified-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${cls}`}>
      <span>{dot}</span> {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {label || "Working…"}
    </div>
  );
}
